// JsonEditor.jsx (Tree modunda x/y sayacı ve Enter/Shift+Enter navigasyonlu)
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Controlled as ControlledEditor } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/theme/darcula.css';
import 'codemirror/theme/elegant.css';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/theme/erlang-dark.css';
import 'codemirror/addon/search/searchcursor';
import 'codemirror/addon/edit/closebrackets';

import { JSONTree } from 'react-json-tree';
import './JsonEditor.css';
import { TbBraces, TbGitFork, TbSearch, TbX } from "react-icons/tb";
import { useLanguage } from '../../LanguageProvider';

function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function countInString(str, q) {
    if (!q) return 0;
    const safe = escapeRegExp(q);
    const re = new RegExp(safe, 'gi');
    let cnt = 0;
    let m;
    while ((m = re.exec(str)) !== null) cnt++;
    return cnt;
}

function countMatches(node, q) {
    if (!q || node == null) return 0;
    let total = 0;

    const t = typeof node;
    if (t === 'string' || t === 'number' || t === 'boolean') {
        total += countInString(String(node), q);
        return total;
    }

    if (Array.isArray(node)) {
        for (const item of node) total += countMatches(item, q);
        return total;
    }

    if (t === 'object') {
        for (const [k, v] of Object.entries(node)) {
            total += countInString(String(k), q);   // key adı içinde eşleşmeler
            total += countMatches(v, q);            // değerlerde eşleşmeler
        }
        return total;
    }

    return total;
}

function JsonTreeView({ json, expandAll, treeResetKey, query, highlightEnabled }) {
    const jsonTreeTheme = useMemo(() => ({
        scheme: 'solarized',
        author: 'Solarized',
        base00: '#0E1326',
        base09: '#5167C9',
        base0B: '#5167C9',
        base0D: '#268bd2',
    }), []);

    const collectionLimit = useMemo(
        () => (highlightEnabled ? Number.MAX_SAFE_INTEGER : 5000),
        [highlightEnabled]
    );

    const highlightMatch = (text) => {
        const str = String(text);
        if (!query || !highlightEnabled) return str;
        const safe = escapeRegExp(query);
        const parts = str.split(new RegExp(`(${safe})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase()
                ? <span key={i} className="jsoneditor-tree-highlight">{part}</span>
                : part
        );
    };

    return (
        <JSONTree
            key={treeResetKey}
            data={json}
            theme={jsonTreeTheme}
            invertTheme={false}
            hideRoot={false}
            shouldExpandNodeInitially={(_keyPath, _data, _level) => !!expandAll}
            labelRenderer={(keyPath) => {
                const label = keyPath && keyPath.length ? keyPath[0] : '';
                return highlightMatch(label);
            }}
            valueRenderer={(value) => highlightMatch(value)}
            collectionLimit={collectionLimit}
        />
    );
}

// 🔒 Auto-close bracket emniyetçisi:
// Seçim yokken "()", "[]", "{}", "''", """" gibi çiftleri ekleyen bir change geldiyse
// caret'i her zaman bu çiftin ORTASINA al.
function ensureCaretInsideAutoClosedPair(editor, data) {
    if (!editor || !data) return;

    // Sadece kullanıcı inputu (+input) ile ilgilen
    if (data.origin !== '+input') return;

    // text: ["()"], ["{}"], ['""'] vb.
    if (!Array.isArray(data.text) || data.text.length !== 1) return;

    const inserted = data.text[0]; // string
    if (typeof inserted !== 'string') return;

    const PAIRS = new Set(['()', '[]', '{}', '""', "''"]);

    if (!PAIRS.has(inserted)) return;

    // Seçim varsa (from != to) otomatik close zaten selection'ı wrap ediyor,
    // o durumda elle dokunmuyoruz.
    const samePos =
        data.from.line === data.to.line &&
        data.from.ch === data.to.ch;

    if (!samePos) return;

    // Bu noktada: seçim yok, tek satır, tam bir çift eklenmiş.
    // Caret'in güvenli noktası: from.ch + 1
    const target = { line: data.from.line, ch: data.from.ch + 1 };

    // Değişiklik uygulandıktan HEMEN sonra caret'i ortaya al.
    // setTimeout 0 ile, CodeMirror değişikliği işledikten sonra pozisyonu override ediyoruz.
    setTimeout(() => {
        try {
            editor.setCursor(target);
        } catch { /* sessizce geç */ }
    }, 0);
}

export default function JsonEditor({ code, setCode, readOnly }) {
    const { getTranslation } = useLanguage();
    const [activeTab, setActiveTab] = useState("text");

    const [showSearch, setShowSearch] = useState(false);
    const [query, setQuery] = useState("");
    const [currentIdx, setCurrentIdx] = useState(0);
    const editorRef = useRef(null);
    const marksRef = useRef([]);

    const [expandAll, setExpandAll] = useState(false);
    const [treeResetKey, setTreeResetKey] = useState(0);

    const treeContainerRef = useRef(null);
    const [treeHits, setTreeHits] = useState([]);
    const [treeIdx, setTreeIdx] = useState(0);

    const treeHighlightEnabled = showSearch && activeTab === 'tree';

    const isEditableEl = useCallback((el) => {
        if (!el) return false;
        if (el.isContentEditable) return true;
        const tag = el.tagName?.toUpperCase?.() || "";
        return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }, []);

    const resetTree = useCallback(() => setTreeResetKey(k => k + 1), []);
    const handleCollapseAll = useCallback(() => { setExpandAll(false); resetTree(); }, [resetTree]);
    const handleExpandAll = useCallback(() => { setExpandAll(true); resetTree(); }, [resetTree]);

    // ❗ ControlledEditor için DOĞRU pattern:
    // onBeforeChange: value state'ini günceller (+ autoCloseBracket emniyeti)
    // onChange: sadece yan etkiler (istersen boş bırak)
    const handleBeforeChange = useCallback((editor, data, value) => {
        if (!readOnly) {
            setCode(value);
        }
        // Auto-close bracket güvenliği:
        ensureCaretInsideAutoClosedPair(editor, data);
    }, [readOnly, setCode]);

    const handleChange = useCallback((_editor, _data, _value) => {
        // Şu an için ekstra bir şey yapmıyoruz.
        // İleride analytics vs. istersen buraya koyarsın.
    }, []);

    const renderSearchCount = () => {
        if (activeTab === 'tree') {
            return treeHits.length > 0 ? `${treeIdx + 1}/${treeHits.length}` : '0/0';
        }
        return marksRef.current.length > 0
            ? `${currentIdx + 1}/${marksRef.current.length}`
            : '0/0';
    };

    const editorOptions = {
        lineNumbers: true,
        mode: 'application/json',
        theme: 'erlang-dark',
        lineWrapping: true,
        readOnly,
        foldGutter: true,
        matchBrackets: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
        // CodeMirror'ın kendi autoCloseBrackets'ı açık:
        autoCloseBrackets: true
    };

    const parsedJson = useMemo(() => {
        try { return JSON.parse(code); } catch { return {}; }
    }, [code]);

    // Text sekmesi: CodeMirror arama işaretlemeleri
    const clearMarks = useCallback(() => {
        marksRef.current.forEach(m => m.clear());
        marksRef.current = [];
    }, []);

    const highlightAll = useCallback((q) => {
        clearMarks();
        if (!q || !editorRef.current) return;
        const cm = editorRef.current;
        const caseFold = q.toLowerCase() === q;
        const cursor = cm.getSearchCursor(q, { line: 0, ch: 0 }, { caseFold });
        while (cursor.findNext()) {
            const m = cm.markText(cursor.from(), cursor.to(), { className: 'jsoneditor-search-hit' });
            marksRef.current.push(m);
        }
        setCurrentIdx(0);
    }, [clearMarks]);

    const revealIndex = useCallback((idx) => {
        if (!editorRef.current || marksRef.current.length === 0) return;
        const cm = editorRef.current;
        const count = marksRef.current.length;
        const safeIdx = ((idx % count) + count) % count;
        const pos = marksRef.current[safeIdx].find();
        if (!pos) return;
        cm.setSelection(pos.from, pos.to);
        cm.scrollIntoView({ from: pos.from, to: pos.to }, 100);
        setCurrentIdx(safeIdx);
    }, []);

    const findNext = useCallback(() => {
        if (marksRef.current.length === 0) highlightAll(query);
        if (marksRef.current.length > 0) revealIndex(currentIdx + 1);
    }, [currentIdx, highlightAll, query, revealIndex]);

    const findPrev = useCallback(() => {
        if (marksRef.current.length === 0) highlightAll(query);
        if (marksRef.current.length > 0) revealIndex(currentIdx - 1);
    }, [currentIdx, highlightAll, revealIndex]);

    // ---- TREE sekmesi: highlight'ları topla ve gezin ----
    const collectTreeHits = useCallback(() => {
        if (!treeContainerRef.current) {
            setTreeHits([]);
            setTreeIdx(0);
            return;
        }
        const nodes = Array.from(
            treeContainerRef.current.querySelectorAll('.jsoneditor-tree-highlight')
        );
        setTreeHits(nodes);
        setTreeIdx(0);

        // İlk hedefi işaretle
        if (nodes.length > 0) {
            nodes.forEach(n => n.classList.remove('jsoneditor-tree-current'));
            nodes[0].classList.add('jsoneditor-tree-current');
            nodes[0].scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }
    }, []);

    const treeRevealIndex = useCallback((idx) => {
        if (treeHits.length === 0) return;
        const count = treeHits.length;
        const safeIdx = ((idx % count) + count) % count;

        // Önceki current'ı temizle
        treeHits.forEach(n => n.classList.remove('jsoneditor-tree-current'));

        const el = treeHits[safeIdx];
        if (!el) return;
        el.classList.add('jsoneditor-tree-current');
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        setTreeIdx(safeIdx);
    }, [treeHits]);

    const treeFindNext = useCallback(() => {
        if (treeHits.length === 0) return;
        treeRevealIndex(treeIdx + 1);
    }, [treeHits.length, treeIdx, treeRevealIndex]);

    const treeFindPrev = useCallback(() => {
        if (treeHits.length === 0) return;
        treeRevealIndex(treeIdx - 1);
    }, [treeHits.length, treeIdx, treeRevealIndex]);

    // Arama barını aç
    const openSearch = useCallback(() => {
        setShowSearch(true);
        // Text sekmesinde highlight başlat
        if (activeTab === 'text') {
            setTimeout(() => highlightAll(query), 0);
        }
        // Tree sekmesinde DOM highlight'ları topla
        if (activeTab === 'tree') {
            setTimeout(() => collectTreeHits(), 0);
        }
    }, [activeTab, highlightAll, collectTreeHits, query]);

    const closeSearch = useCallback(() => {
        setShowSearch(false);
        clearMarks();
        if (treeContainerRef.current) {
            treeContainerRef.current
                .querySelectorAll('.jsoneditor-tree-current')
                .forEach(n => n.classList.remove('jsoneditor-tree-current'));
        }
        resetTree();
    }, [clearMarks, resetTree]);

    // Global kısayollar
    useEffect(() => {
        const onKeyDown = (e) => {
            if (isEditableEl(e.target)) return;

            const isFind = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f';
            if (isFind) { e.preventDefault(); openSearch(); }

            if (showSearch && e.key === 'Escape') { e.preventDefault(); closeSearch(); }

            // Enter / Shift+Enter: aktif sekmeye göre gezin
            if (showSearch && e.key === 'Enter') {
                e.preventDefault();
                if (activeTab === 'tree') {
                    if (e.shiftKey) treeFindPrev(); else treeFindNext();
                } else {
                    if (e.shiftKey) findPrev(); else findNext();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [openSearch, closeSearch, showSearch, activeTab, treeFindNext, treeFindPrev, findNext, findPrev, isEditableEl]);

    useEffect(() => {
        if (!showSearch) return;
        if (activeTab === 'tree') {
            if (query && query.trim()) {
                setExpandAll(true);
                resetTree();
                const t = setTimeout(() => collectTreeHits(), 0);
                return () => clearTimeout(t);
            } else {
                setTreeHits([]);
                setTreeIdx(0);
                if (treeContainerRef.current) {
                    treeContainerRef.current
                        .querySelectorAll('.jsoneditor-tree-current')
                        .forEach(n => n.classList.remove('jsoneditor-tree-current'));
                }
            }
        }
    }, [query, activeTab, showSearch, resetTree, collectTreeHits]);

    // Sekme değiştiğinde metin/DOM highlight state'lerini sıfırla/yenile
    useEffect(() => {
        if (activeTab !== 'text') {
            clearMarks();
        } else if (showSearch) {
            highlightAll(query);
        }

        if (activeTab === 'tree') {
            setTimeout(() => collectTreeHits(), 0);
        } else {
            setTreeHits([]);
            setTreeIdx(0);
        }
    }, [activeTab, clearMarks, highlightAll, query, showSearch, collectTreeHits]);

    // Tree: manual expand/collapse yapıldığında DOM'u yeniden tara
    useEffect(() => {
        if (activeTab === 'tree' && showSearch) {
            const t = setTimeout(() => collectTreeHits(), 0);
            return () => clearTimeout(t);
        }
    }, [treeResetKey, activeTab, showSearch, collectTreeHits]);

    // Tree’de query varsa otomatik expand
    useEffect(() => {
        if (activeTab === 'tree') {
            if (query && query.trim().length > 0) {
                setExpandAll(true);
                resetTree();
            }
        }
    }, [query, activeTab, resetTree]);

    return (
        <div style={{ maxWidth: '100%', height: '100%', position: 'relative' }}>
            {/* Üst butonlar */}
            <div style={{ width: '100%', justifyContent: 'flex-end', height: '10%', flexDirection: 'row', display: 'flex', gap: '5px', zIndex: 2, position: 'relative' }}>
                <button className={"format-button"} onClick={() => setActiveTab('text')}>
                    <TbBraces size={16} /> JSON
                </button>
                <button className={"format-button"} onClick={() => setActiveTab('tree')}>
                    <TbGitFork size={16} /> Tree
                </button>
                <button className="format-button search-icon-button" onClick={openSearch}>
                    <TbSearch size={16} />
                </button>
            </div>

            {/* Arama barı */}
            {showSearch && (
                <div className="jsoneditor-search-bar">
                    <TbSearch size={16} className="search-icon-static" />
                    <input
                        autoFocus
                        className="jsoneditor-search-input"
                        placeholder={getTranslation("search")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();

                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (activeTab === 'tree') treeFindNext(); else findNext();
                            } else if (e.key === 'Enter' && e.shiftKey) {
                                e.preventDefault();
                                if (activeTab === 'tree') treeFindPrev(); else findPrev();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                closeSearch();
                            }
                        }}
                    />
                    <div className="jsoneditor-search-actions">
                        <button className="search-nav-btn" onClick={() => (activeTab === 'tree' ? treeFindPrev() : findPrev())}>↑</button>
                        <button className="search-nav-btn" onClick={() => (activeTab === 'tree' ? treeFindNext() : findNext())}>↓</button>
                        <span className="search-count">{renderSearchCount()}</span>
                        <button className="search-close-btn" onClick={closeSearch}>
                            <TbX size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Tree toolbar */}
            {activeTab === 'tree' && (
                <div className="json-tree-toolbar">
                    <button
                        className="format-button"
                        onClick={() => {
                            setExpandAll(prev => !prev);
                            resetTree();
                        }}
                    >
                        {expandAll ? getTranslation("hide") : getTranslation("show")}
                    </button>
                </div>
            )}

            {/* Editörler */}
            {activeTab === "text" && (
                <div style={{ height: '90%', overflowX: 'auto' }}>
                    <ControlledEditor
                        value={code}
                        options={editorOptions}
                        onBeforeChange={handleBeforeChange}
                        onChange={handleChange}
                        editorDidMount={(editor) => { editorRef.current = editor; }}
                        editorWillUnmount={() => { editorRef.current = null; }}
                    />
                </div>
            )}

            {activeTab === "tree" && (
                <div
                    ref={treeContainerRef}
                    style={{ height: '90%', overflowX: 'auto' }}
                    className='json-tree-container'
                >
                    <JsonTreeView
                        json={parsedJson}
                        expandAll={expandAll}
                        treeResetKey={treeResetKey}
                        query={query}
                        highlightEnabled={treeHighlightEnabled}
                    />
                </div>
            )}
        </div>
    );
}